import { isValidPhoneNumber } from 'react-phone-number-input'

import { TIN_LENGTH, TIN_IE_LENGTH, KPP_LENGTH, OGRN_LENGTH, OGRN_IE_LENGTH } from 'const';
import { IValidator, IValidationErrors, ValidationTypes, IValidateForm, IValidateFormReturn } from 'interfaces/validator';

const EMPTY_FIELD_ERROR = 'Необходимо заполнить поле';

const VALIDATION_ERRORS: IValidationErrors = {
	EMPTY: {
		email: 'Необходимо указать почту',
		password: 'Необходимо ввести пароль',
		confirmPassword: 'Необходимо ввести пароль',
		oldPassword: 'Необходимо ввести пароль',
		tin: 'Необходимо ввести ИНН',
		kpp: 'Необходимо ввести КПП',
		ogrn: 'Необходимо ввести ОГРН',
	},
	INVALID: {
		email: 'Некорректная почта',
		password: 'Неверный пароль',
		confirmPassword: 'Пароль не совпадает',
		oldPassword: 'Неверный пароль',
		name: 'Только буквы кириллицы',
		phone: 'Неверный номер',
		tin: 'Неверный ИНН',
		kpp: 'Неверный КПП',
		ogrn: 'Неверный ОГРН',
		login: 'Логин может состоять из латинских символов, цифр или одинарного дефиса',
		url: 'Неверный адрес сайта'
	},
	LENGTH: {
		name: 'Не более 30 символов',
		password: 'Слишком короткий пароль',
		oldPassword: 'Слишком короткий пароль',
		login: 'Не более 30 символов',
	},
};

export const VALIDATION_TYPES: {[key: string]: ValidationTypes} = {
	EMAIL: 'email',
	PASSWORD: 'password',
	CONFIRM_PASSWORD: 'confirmPassword',
	OLD_PASSWORD: 'oldPassword',
	FIRST_NAME: 'firstName',
	LAST_NAME: 'lastName',
	MIDDLE_NAME: 'middleName',
	NAME: 'name',
	PHONE: 'phone',
	TIN: 'tin',
	KPP: 'kpp',
	OGRN: 'ogrn',
	LOGIN: 'login',
	URL: 'url',
};

export const validateEmail = (email: string): string => {
	// eslint-disable-next-line
	const re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	const isValid = re.test(email.toLowerCase());
	return  isValid ? '' : VALIDATION_ERRORS.INVALID[VALIDATION_TYPES.EMAIL];
};

export const validatePassword = (type: string, password: string, confirmPassword?: string): string => {
	if (confirmPassword && password !== confirmPassword) {
		return VALIDATION_ERRORS.INVALID[type];
	}
	if (password.length < 8) return VALIDATION_ERRORS.LENGTH[type];
	const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[-_,.!*\\])/;
	const isValid= !!confirmPassword || re.test(password);
	return isValid ? '' : VALIDATION_ERRORS.INVALID[type];
};

export const validateUserName = (name: string): string => {
	if (name.length > 30) return VALIDATION_ERRORS.LENGTH[VALIDATION_TYPES.NAME];
	const re = /^[А-Яа-яЁё -]+$/;
	const isCyrillic = re.test(name);
	return  isCyrillic ? '' : VALIDATION_ERRORS.INVALID[VALIDATION_TYPES.NAME];
};

export const validatePhone = (phone: string): string => {
	const isValid = !phone || isValidPhoneNumber(phone);
	return  isValid ? '' : VALIDATION_ERRORS.INVALID[VALIDATION_TYPES.PHONE];
};

export const validateTin = (tin: string): string => {
	const isValid = tin.length === TIN_LENGTH || tin.length === TIN_IE_LENGTH;
	return  isValid ? '' : VALIDATION_ERRORS.INVALID[VALIDATION_TYPES.TIN];
};

export const validateKpp = (kpp: string): string => {
	if (!kpp) return '';
	const isValid = kpp.length === KPP_LENGTH;
	return  isValid ? '' : VALIDATION_ERRORS.INVALID[VALIDATION_TYPES.KPP];
};

export const validateOgrn = (ogrn: string, isIe?: boolean): string => {
	const isValid = isIe ? ogrn.length === OGRN_IE_LENGTH : ogrn.length === OGRN_LENGTH;
	return  isValid ? '' : VALIDATION_ERRORS.INVALID[VALIDATION_TYPES.OGRN];
};

export const validateLogin = (login: string): string => {
	if (login.length > 30) return VALIDATION_ERRORS.LENGTH[VALIDATION_TYPES.LOGIN];
	const re = /^[a-zA-Z]([a-zA-Z0-9-]+)?[a-zA-Z0-9]$/;
	const isValid = re.test(login);
	return  isValid ? '' : VALIDATION_ERRORS.INVALID[VALIDATION_TYPES.LOGIN];
};

export const validateUrl = (url: string): string => {
	const re = /^([\da-z])([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
	const isValid = re.test(url);
	return  isValid ? '' : VALIDATION_ERRORS.INVALID[VALIDATION_TYPES.URL];
};

export const validateValue = ({ type, value, isRequired, confirmValue }: IValidator): string => {
	if (!value?.length) {
		return isRequired ? VALIDATION_ERRORS.EMPTY[type] || EMPTY_FIELD_ERROR : '';
	}

	switch (type) {
		case VALIDATION_TYPES.EMAIL:
			return validateEmail(value);

		case VALIDATION_TYPES.PASSWORD:
		case VALIDATION_TYPES.CONFIRM_PASSWORD:
		case VALIDATION_TYPES.OLD_PASSWORD:
			return validatePassword(type, value, confirmValue as string);

		case VALIDATION_TYPES.FIRST_NAME:
		case VALIDATION_TYPES.LAST_NAME:
		case VALIDATION_TYPES.MIDDLE_NAME:
			return validateUserName(value);

		case VALIDATION_TYPES.PHONE:
			return validatePhone(value);

		case VALIDATION_TYPES.TIN:
			return validateTin(value);

		case VALIDATION_TYPES.KPP:
			return validateKpp(value);

		case VALIDATION_TYPES.OGRN:
			return validateOgrn(value, confirmValue as boolean);

		case VALIDATION_TYPES.LOGIN:
			return validateLogin(value);

		case VALIDATION_TYPES.URL:
			return validateUrl(value);

		default:
			return '';
	}
};

export const validateForm = ({ formData, requiredFields = [], isRequired = false, confirmValues = {} }: IValidateForm): IValidateFormReturn => {
	let isValid = true;
	const formErrors: { [key: string]: string } = {};
	const formattedFormData = Object.keys(formData).map((key) => ({
		type: key,
		value: formData[key],
		isRequired: isRequired || requiredFields.includes(key),
		confirmValue: confirmValues[key] || '',
	}));
	formattedFormData.forEach((formValue) => {
		const error = validateValue(formValue);
		formErrors[formValue.type] = error;
		if (isValid && error) {
			isValid = false;
		}
	});
	return { isValid, formErrors };
};
